import { useState } from 'react';
import { Button, Form, Input, message } from 'antd';
import { IconUser, IconLock } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/contexts/BrandingContext';

interface LocationState {
  from?: string;
}

const _PLATFORM_DEFAULT_NAMES = new Set([
  '__default__',
  '锚点科技 · 孵化器一体化运营平台',
]);

export function LoginPage() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { branding } = useBranding();
  const [form] = Form.useForm();

  // 是否有真实租户白标(已初始化 tenant_branding)— false 时显示通用平台标题
  const hasTenantBranding =
    !_PLATFORM_DEFAULT_NAMES.has(branding.tenant_slug ?? '') &&
    !_PLATFORM_DEFAULT_NAMES.has(branding.company_name ?? '');

  const headerCompany = hasTenantBranding
    ? branding.company_name ?? '孵化器一体化运营平台'
    : '孵化器一体化运营平台';
  const headerSubtitle = hasTenantBranding
    ? branding.tagline ?? '锚定确定性 · 解析无限可能'
    : '锚定确定性 · 解析无限可能';

  // 顶部 logo:仅当租户白标有 logo_url(http/https)才显示;无则纯标题不占位
  const tenantLogoUrl =
    hasTenantBranding && branding.logo_url && /^https?:\/\//i.test(branding.logo_url)
      ? branding.logo_url
      : null;

  const primaryColor = branding.primary_color ?? 'var(--color-primary)';

  // R3-Sprint12-A-UX7:登录页背景图真接通(白标 login_bg_url 字段)
  // 仅接 http(s) 防 javascript:/data: 协议;失败由浏览器静默(背景空)
  const loginBgUrl =
    branding.login_bg_url && /^https?:\/\//i.test(branding.login_bg_url)
      ? branding.login_bg_url
      : null;
  // CSS injection 防御:防 url 含引号/分号截断 background 属性
  const safeBg = loginBgUrl && !/["'<>\s();]/.test(loginBgUrl) ? loginBgUrl : null;

  const onSubmit = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      // R3-Sprint12-A UX:不再传 tenant_code — 后端按 username 解析,
      // useAuth.login 收到 res.tenant_slug 后 setTenantCode + 派发事件
      await login(values);
      message.success('登录成功');
      // Sec Reviewer high#2：redirect 必须本站白名单 — 防 location.state 被注入外站/javascript: URL
      const rawTo = (location.state as LocationState | null)?.from;
      // Sprint 32-E + Sprint 34-H: 按角色派落地页
      // - staff/tenant_admin/manager → /my-todos(系统推待办)
      // - enterprise → /me/enterprise(企业主门户 — 第一眼看到机会/可申报资质)
      // - mentor    → /me/mentor(导师门户)
      // - investor  → /me/investor(暂保留路由,Sprint 34 已下线邀请通道)
      // - platform_operator + 兜底 → /dashboard
      const role = localStorage.getItem('role');
      let defaultLanding = '/dashboard';
      if (role === 'staff' || role === 'tenant_admin' || role === 'manager') {
        defaultLanding = '/my-todos';
      } else if (role === 'enterprise') {
        defaultLanding = '/me/enterprise';
      } else if (role === 'mentor') {
        defaultLanding = '/me/mentor';
      } else if (role === 'investor') {
        defaultLanding = '/me/investor';
      }
      // Sprint42-hotfix:子门户角色(enterprise/mentor/investor)无 sidebar,
      // 任何 location.state.from 必是他们没权限的业务页(如未登录访问 /dashboard 被弹回登录,
      // 登录后回跳 /dashboard 即触发 RoleGuard 403 "当前角色无权访问该页面")。
      // 强制走 defaultLanding 即各自的子门户。
      const isSubportalRole =
        role === 'enterprise' || role === 'mentor' || role === 'investor';
      const safeTo =
        !isSubportalRole &&
        typeof rawTo === 'string' &&
        rawTo.startsWith('/') &&
        !rawTo.startsWith('//')
          ? rawTo
          : defaultLanding;
      navigate(safeTo, { replace: true });
    } catch {
      // axios 拦截器已 message.error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 26,
        background: safeBg
          ? `center / cover no-repeat url("${safeBg}"), var(--color-bg-page)`
          : 'var(--color-bg-page)',
      }}
    >
      <div style={{ maxWidth: 380, width: '100%' }}>
        <div
          style={{
            background: 'var(--color-bg-page-warm)',
            borderRadius: 'var(--radius-2xl)',
            padding: 18,
          }}
        >
          {/* === 登录表单 === */}
          <div
            style={{
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-xl)',
              padding: '20px 18px',
            }}
          >
            {/* 顶部品牌区 — 跟 tenant 白标走;无 logo 时不占位(纯标题左对齐) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              {tenantLogoUrl && (
                <img
                  src={tenantLogoUrl}
                  alt={headerCompany}
                  width={48}
                  height={48}
                  style={{ borderRadius: '50%', flex: 'none', objectFit: 'contain' }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{headerCompany}</div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--color-text-tertiary)',
                    marginTop: 2,
                  }}
                >
                  {headerSubtitle}
                </div>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              requiredMark={false}
            >
              <Form.Item
                label="账号"
                name="username"
                rules={[{ required: true, message: '请输入账号' }]}
              >
                <Input
                  prefix={<IconUser size={14} />}
                  placeholder="username@tenant"
                  autoComplete="username"
                />
              </Form.Item>
              <Form.Item
                label="密码"
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<IconLock size={14} />}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  height: 40,
                  background: `linear-gradient(135deg, ${primaryColor}, var(--color-brand-indigo))`,
                  border: 'none',
                  fontWeight: 500,
                }}
                data-testid="login-submit"
              >
                登录
              </Button>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: 'var(--color-text-tertiary)',
                  textAlign: 'center',
                }}
              >
                JWT · 登录后按角色进入对应工作台
              </div>
            </Form>
          </div>

          {/* === 底部:锚点科技 × 圣丰联智谷 联合出品(双 logo) === */}
          <div
            style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 6,
              fontSize: 11,
              color: 'var(--color-text-tertiary)',
            }}
          >
            <img
              src="/anchortech-logo.png"
              alt="AnchorTech"
              width={20}
              height={20}
              style={{ borderRadius: '50%', flex: 'none' }}
            />
            <span style={{ fontWeight: 500 }}>锚点科技</span>
            <span style={{ opacity: 0.5 }}>×</span>
            <img
              src="/sfl-logo.jpg"
              alt="圣丰联智谷"
              width={20}
              height={20}
              style={{ borderRadius: 4, flex: 'none', objectFit: 'contain' }}
            />
            <span style={{ fontWeight: 500 }}>圣丰联智谷</span>
            <span style={{ marginLeft: 4, opacity: 0.7 }}>联合出品</span>
          </div>
        </div>
      </div>
    </div>
  );
}
